import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../context/ApiContext';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';

function money(value) {
  const amount = Number(value || 0);
  return `R${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusVariant(row) {
  if (row.needsInvoice) return 'gray';
  if (row.isOverdue) return 'red';

  switch (row.status) {
    case 'paid':
      return 'green';
    case 'partial':
      return 'orange';
    case 'pending_review':
      return 'amber';
    case 'issued':
      return 'blue';
    case 'declined':
      return 'red';
    default:
      return 'gray';
  }
}

export default function BillingAdmin() {
  const navigate = useNavigate();
  const {
    adminGetBillingOverview,
    adminGenerateBillingInvoices,
    adminSendBillingReminder,
    adminSendBillingBulkReminders,
    adminRecordBillingPayment,
  } = useApi();

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ summary: {}, items: [], statusOptions: [] });
  const [generating, setGenerating] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentRow, setPaymentRow] = useState(null);
  const [paymentFiles, setPaymentFiles] = useState([]);
  const [paymentError, setPaymentError] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    submittedByName: '',
    submittedByEmail: '',
    paymentReference: '',
    amountClaimed: '',
    approvedAmount: '',
    notes: '',
    reviewNotes: '',
  });

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const next = await adminGetBillingOverview({ month, search: search || undefined, status: status || undefined });
        if (active) setData(next || { summary: {}, items: [], statusOptions: [] });
      } catch (e) {
        if (active) setError(e?.message || 'Failed to load billing workspace');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [adminGetBillingOverview, month, search, status]);

  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);
  const statusOptions = Array.isArray(data?.statusOptions) ? data.statusOptions : [];
  const overdueInvoiceIds = useMemo(
    () => items.filter((row) => row.isOverdue && row.invoice?.canSendReminder).map((row) => row.invoice.id),
    [items],
  );

  async function refresh() {
    try {
      setLoading(true);
      setError('');
      const next = await adminGetBillingOverview({ month, search: search || undefined, status: status || undefined });
      setData(next || { summary: {}, items: [], statusOptions: [] });
    } catch (e) {
      setError(e?.message || 'Failed to load billing workspace');
    } finally {
      setLoading(false);
    }
  }

  async function generateInvoices() {
    try {
      setGenerating(true);
      const res = await adminGenerateBillingInvoices({ month });
      alert(res?.message || 'Invoices generated');
      await refresh();
    } catch (e) {
      alert(e?.message || 'Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  }

  async function sendReminder(invoiceId) {
    try {
      setSendingReminderId(invoiceId);
      const res = await adminSendBillingReminder({ invoiceId });
      alert(res?.message || 'Reminder sent');
      await refresh();
    } catch (e) {
      alert(e?.message || 'Failed to send reminder');
    } finally {
      setSendingReminderId(null);
    }
  }

  async function sendOverdueReminders() {
    if (!overdueInvoiceIds.length) return;

    try {
      setSendingBulk(true);
      const res = await adminSendBillingBulkReminders({ invoiceIds: overdueInvoiceIds });
      alert(res?.message || 'Overdue reminders sent');
      await refresh();
    } catch (e) {
      alert(e?.message || 'Failed to send overdue reminders');
    } finally {
      setSendingBulk(false);
    }
  }

  function openPaymentModal(row) {
    if (!row?.invoice) return;
    const outstanding = row.invoice?.outstandingAmount != null ? String(row.invoice.outstandingAmount) : '';
    setPaymentRow(row);
    setPaymentFiles([]);
    setPaymentError('');
    setPaymentForm({
      submittedByName: row.tenantName || '',
      submittedByEmail: row.tenantEmail || '',
      paymentReference: '',
      amountClaimed: outstanding,
      approvedAmount: outstanding,
      notes: '',
      reviewNotes: '',
    });
    setShowPaymentModal(true);
  }

  function closePaymentModal() {
    setShowPaymentModal(false);
    setPaymentRow(null);
    setPaymentFiles([]);
    setPaymentError('');
  }

  function onPaymentChange(e) {
    const { name, value } = e.target;
    setPaymentForm((current) => ({ ...current, [name]: value }));
  }

  async function submitPayment(markAsPaid) {
    if (!paymentRow?.invoice?.id) return;

    try {
      setRecordingPayment(true);
      setPaymentError('');
      const res = await adminRecordBillingPayment({
        invoiceId: paymentRow.invoice.id,
        submittedByName: paymentForm.submittedByName || undefined,
        submittedByEmail: paymentForm.submittedByEmail || undefined,
        paymentReference: paymentForm.paymentReference || undefined,
        amountClaimed: paymentForm.amountClaimed || undefined,
        approvedAmount: paymentForm.approvedAmount || undefined,
        notes: paymentForm.notes || undefined,
        reviewNotes: paymentForm.reviewNotes || undefined,
        markAsPaid,
        files: paymentFiles,
      });
      alert(res?.message || (markAsPaid ? 'Payment recorded.' : 'Proof uploaded.'));
      closePaymentModal();
      await refresh();
    } catch (e) {
      setPaymentError(e?.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  }

  const summary = data?.summary || {};

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-gray-600">Track active tenants, current-month invoices, overdue balances, and send reminders without leaving the React admin.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={refresh} disabled={loading}>Refresh</Button>
          <Button variant="secondary" onClick={sendOverdueReminders} disabled={sendingBulk || overdueInvoiceIds.length === 0}>
            {sendingBulk ? 'Sending…' : `Remind Overdue (${overdueInvoiceIds.length})`}
          </Button>
          <Button variant="primary" onClick={generateInvoices} disabled={generating}>
            {generating ? 'Generating…' : 'Generate & Email Invoices'}
          </Button>
        </div>
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-xl bg-white p-4 shadow">
          <div className="text-sm text-gray-500">Active tenants</div>
          <div className="mt-1 text-2xl font-semibold">{summary.activeTenants || 0}</div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow">
          <div className="text-sm text-gray-500">Invoiced</div>
          <div className="mt-1 text-2xl font-semibold">{summary.invoiced || 0}</div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow">
          <div className="text-sm text-gray-500">Overdue</div>
          <div className="mt-1 text-2xl font-semibold text-red-700">{summary.overdue || 0}</div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow">
          <div className="text-sm text-gray-500">Outstanding</div>
          <div className="mt-1 text-2xl font-semibold">{money(summary.outstandingAmount || 0)}</div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow">
          <div className="text-sm text-gray-500">Month</div>
          <div className="mt-1 text-lg font-semibold">{data?.monthLabel || 'Current month'}</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-xl bg-white p-4 shadow xl:flex-row">
        <input
          className="border rounded px-3 py-2 xl:min-w-[280px]"
          placeholder="Search tenant, email, property, unit, invoice"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <input
          type="month"
          className="border rounded px-3 py-2"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        <select className="border rounded px-3 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statusOptions.map((option) => (
            <option key={option.value || 'all'} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <div className="rounded-xl bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-left">Property / Unit</th>
                <th className="px-3 py-2 text-left">Monthly Rent</th>
                <th className="px-3 py-2 text-left">Invoice</th>
                <th className="px-3 py-2 text-left">Due</th>
                <th className="px-3 py-2 text-left">Outstanding</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`billing-skel-${i}`} className="border-t">
                    <td className="px-3 py-3"><div className="h-4 w-40 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-32 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-24 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-32 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-20 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-24 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-20 animate-pulse rounded bg-gray-200" /></td>
                    <td className="px-3 py-3 text-right"><div className="ml-auto h-8 w-24 animate-pulse rounded bg-gray-200" /></td>
                  </tr>
                ))
              )}
              {items.map((row) => (
                <tr key={row.tenantId} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <button className="font-medium hover:underline" onClick={() => navigate(`/admin/tenants/${encodeURIComponent(row.tenantId)}`)}>
                      {row.tenantName}
                    </button>
                    <div className="text-xs text-gray-500">{row.tenantEmail || 'No email'}{row.tenantPhone ? ` • ${row.tenantPhone}` : ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{row.propertyName || row.propertySlug || 'Unknown property'}</div>
                    <div className="text-xs text-gray-500">{row.unitCode || row.unitId || 'No unit'} • {row.tenantStatus}</div>
                  </td>
                  <td className="px-3 py-2">{money(row.monthlyRent)}</td>
                  <td className="px-3 py-2">
                    {row.invoice ? (
                      <>
                        <div className="font-medium text-gray-900">{row.invoice.invoiceNumber}</div>
                        <div className="text-xs text-gray-500">Issued {row.invoice.issuedOn || '—'}</div>
                      </>
                    ) : (
                      <span className="text-xs text-amber-700">Not billed yet</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.invoice ? (
                      <>
                        <div>{row.invoice.dueOn || 'TBD'}</div>
                        {row.isOverdue && (
                          <div className="text-xs text-red-700">{row.daysOverdue} day{row.daysOverdue === 1 ? '' : 's'} overdue</div>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-500">Generate invoice first</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{money(row.invoice?.outstandingAmount || 0)}</div>
                    {row.invoice && <div className="text-xs text-gray-500">Paid {money(row.invoice.amountPaid || 0)}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={statusVariant(row)}>{row.statusLabel}</Badge>
                      {row.isOverdue && <Badge variant="red">Overdue</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => navigate(`/admin/tenants/${encodeURIComponent(row.tenantId)}`)}>
                        View
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={!row.invoice || row.status === 'paid' || row.status === 'void'}
                        onClick={() => openPaymentModal(row)}
                      >
                        Record Payment
                      </Button>
                      <Button
                        variant="primary"
                        disabled={!row.canSendReminder || sendingReminderId === row.invoice?.id}
                        onClick={() => row.invoice?.id && sendReminder(row.invoice.id)}
                      >
                        {sendingReminderId === row.invoice?.id ? 'Sending…' : 'Send Reminder'}
                      </Button>
                    </div>
                    {row.invoice?.lastEmailedAt && (
                      <div className="mt-1 text-xs text-gray-500">Last sent {new Date(row.invoice.lastEmailedAt).toLocaleString()}</div>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr className="border-t">
                  <td className="px-3 py-8 text-center text-gray-600" colSpan={8}>No billing rows match this filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPaymentModal && paymentRow?.invoice && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={closePaymentModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Record Payment</h2>
                  <p className="text-sm text-gray-600">
                    {paymentRow.tenantName} • {paymentRow.invoice.invoiceNumber} • Outstanding {money(paymentRow.invoice.outstandingAmount || 0)}
                  </p>
                </div>
                <button className="rounded border px-3 py-2 text-sm" onClick={closePaymentModal}>Close</button>
              </div>

              <div className="grid gap-4 p-5 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700">Submitted By Name</span>
                  <input className="border rounded px-3 py-2" name="submittedByName" value={paymentForm.submittedByName} onChange={onPaymentChange} />
                </label>
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700">Submitted By Email</span>
                  <input className="border rounded px-3 py-2" name="submittedByEmail" value={paymentForm.submittedByEmail} onChange={onPaymentChange} />
                </label>
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700">Payment Reference</span>
                  <input className="border rounded px-3 py-2" name="paymentReference" value={paymentForm.paymentReference} onChange={onPaymentChange} placeholder="Bank reference or EFT ref" />
                </label>
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700">Claimed Amount</span>
                  <input className="border rounded px-3 py-2" type="number" min="0" step="0.01" name="amountClaimed" value={paymentForm.amountClaimed} onChange={onPaymentChange} />
                </label>
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700">Approved Amount</span>
                  <input className="border rounded px-3 py-2" type="number" min="0" step="0.01" name="approvedAmount" value={paymentForm.approvedAmount} onChange={onPaymentChange} />
                </label>
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700">Proof Files</span>
                  <input
                    className="border rounded px-3 py-2"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setPaymentFiles(Array.from(e.target.files || []))}
                  />
                </label>
                <label className="grid gap-2 md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Submission Note</span>
                  <textarea className="min-h-[100px] rounded border px-3 py-2" name="notes" value={paymentForm.notes} onChange={onPaymentChange} placeholder="Optional note about the payment proof" />
                </label>
                <label className="grid gap-2 md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Review Note</span>
                  <textarea className="min-h-[100px] rounded border px-3 py-2" name="reviewNotes" value={paymentForm.reviewNotes} onChange={onPaymentChange} placeholder="Optional internal note when marking paid" />
                </label>
              </div>

              {paymentFiles.length > 0 && (
                <div className="px-5 pb-1 text-sm text-gray-600">
                  {paymentFiles.length} file{paymentFiles.length === 1 ? '' : 's'} selected: {paymentFiles.map((file) => file.name).join(', ')}
                </div>
              )}
              {paymentError && (
                <div className="px-5 pb-1 text-sm text-red-700">{paymentError}</div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2 border-t px-5 py-4">
                <Button variant="secondary" onClick={closePaymentModal} disabled={recordingPayment}>Cancel</Button>
                <Button variant="secondary" onClick={() => submitPayment(false)} disabled={recordingPayment}>
                  {recordingPayment ? 'Saving…' : 'Upload Proof Only'}
                </Button>
                <Button variant="primary" onClick={() => submitPayment(true)} disabled={recordingPayment}>
                  {recordingPayment ? 'Saving…' : 'Upload & Mark Paid'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
